/**
 * usePlayerSearch Hook
 *
 * Provides player search functionality via the API.
 * Searches users by displayName with debouncing support.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Search result player type
 */
export interface SearchResultPlayer {
  id: string;
  displayName: string;
  stats: {
    wins: number;
    losses: number;
  };
}

/**
 * Search response from API
 */
interface SearchResponse {
  users: SearchResultPlayer[];
  total: number;
}

/**
 * Hook return type
 */
export interface UsePlayerSearchReturn {
  /** Execute a search with the given query */
  search: (query: string) => Promise<void>;
  /** Current search results */
  results: SearchResultPlayer[];
  /** Whether a search is in progress */
  isSearching: boolean;
  /** Error message if search failed */
  error: string | null;
  /** Clear all results and error state */
  clearResults: () => void;
}

/**
 * Hook for searching players by displayName
 *
 * @param limit - Maximum number of results (default: 10)
 */
export function usePlayerSearch(limit: number = 10): UsePlayerSearchReturn {
  const { getIdToken } = useAuth();
  const [results, setResults] = useState<SearchResultPlayer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Execute a search
   */
  const search = useCallback(async (query: string): Promise<void> => {
    // Don't search for empty or very short queries
    if (!query || query.trim().length < 2) {
      setResults([]);
      setError(null);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(
        `${apiUrl}/api/users/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Search failed' }));
        throw new Error(errorData.error || `Search failed with status ${response.status}`);
      }

      const data: SearchResponse = await response.json();
      setResults(data.users);
    } catch (err) {
      console.error('[usePlayerSearch] Search failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to search players');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [getIdToken, limit]);

  /**
   * Clear results and error state
   */
  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    search,
    results,
    isSearching,
    error,
    clearResults
  };
}
